import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { AppointmentStatus, AppointmentType } from '@bb/domain/dist/types';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import type { ColumnsType } from 'antd/es/table';

import {
  Empty,
  Icon,
  Input,
  Link,
  ListingImage,
  Pagination,
  Table,
  Tabs,
  Text,
  message,
} from 'components';
import { CrossOrgAnsweringContext } from 'contexts';
import { ShowingCalendarController } from 'api';
import type { AppointmentQueryType } from 'api/ShowingCalendarController/findAppointmentsByUserId';
import { useDebounce } from 'hooks';
import {
  CHAT_SESSION_STORAGE_KEYS,
  buildListingFullAddress,
  paramaterize,
  formatDateStrWithYearShort,
  formatTimeStrLowerCase,
  featureEnabled,
} from 'utils';
import errorReporter from '@app/errorReporter';

import { Section, StyledCard } from '../styles';
import { StatusEntry } from '../../../components';
import MLSUserPageContext from '../MLSUserPageContext';

const SHOWING_PAGE_SIZE = 20;

type ShowingRecordType = {
  time: string;
  timeEnd: string;
  status?: AppointmentStatus;
  displayStatus: string;
  type: AppointmentType | 'APPOINTMENT' | 'ROUTE_STOP';
  listingContactTimeChange?: boolean;
  listing: {
    line1: string;
    line2?: string;
    thumbnail?: string;
  };
};

const columns: ColumnsType<ShowingRecordType> = [
  {
    key: 'listingDetail',
    dataIndex: ['listing', 'thumbnail'],
    render: (thumbnail, { listing: { line1, line2 = '' } }) => (
      <StyledContainer>
        <ListingImage imageSrc={thumbnail} size={24} fixProportion />
        {buildListingFullAddress(line1, line2)}
      </StyledContainer>
    ),
  },
  {
    dataIndex: 'time',
    width: 200,
    render: (time, { timeEnd }) => (
      <>
        {formatDateStrWithYearShort(time)}
        <br />
        {formatTimeStrLowerCase(time)} - {formatTimeStrLowerCase(timeEnd)}
      </>
    ),
  },
  {
    dataIndex: 'status',
    width: 200,
    render: (status, { displayStatus }) => (
      <StatusEntry isBuyer showTagOnly displayStatus={displayStatus} />
    ),
  },
  {
    dataIndex: 'type',
  },
];

type TabKeyType = AppointmentQueryType;
type ShowingSectionProps = {};

const ShowingSection: React.FC<ShowingSectionProps> = () => {
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext) || {};
  const { user, userId } = useContext(MLSUserPageContext);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText);

  const [loading, setLoading] = useState(false);
  const [tabKey, setTabKey] = useState<TabKeyType>('buyer');
  const [tablePage, setTablePage] = useState(1);
  const [showings, setShowings] = useState<ShowingRecordType[]>([]);
  const [totalShowingCount, setTotalShowingCount] = useState(0);

  const totalNumberOfPages = Math.ceil(totalShowingCount / SHOWING_PAGE_SIZE);

  useEffect(() => {
    (async () => {
      if (userId) {
        setLoading(true);
        try {
          const result =
            await ShowingCalendarController.findAppointmentsByUserId(userId, {
              page: tablePage,
              type: tabKey,
              searchText: debouncedSearchText,
            });
          setShowings(result.appointments);
          setTotalShowingCount(result.count);
        } catch (err) {
          errorReporter.error(err);
          message.error('Failed to load showings data');
          setShowings([]);
          setTotalShowingCount(0);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [tabKey, tablePage, userId, debouncedSearchText, user?.coopType]);

  if (!userId) return null;

  const showingTable = (
    <>
      <Table
        columns={columns}
        dataSource={showings}
        pagination={false}
        scroll={{ y: 300 }}
        loading={loading}
        showHeader={false}
        locale={{
          emptyText: <Empty description={`There are no ${tabKey} showings.`} />,
        }}
        rowKey="id"
      />
      {totalNumberOfPages > 1 && (
        <StyledPagination
          current={tablePage}
          pageSize={SHOWING_PAGE_SIZE}
          total={totalShowingCount}
          onChange={page => setTablePage(page)}
          showSizeChanger={false}
        />
      )}
    </>
  );

  const tabItems = [
    { label: 'Buyer', key: 'buyer', children: showingTable },
    { label: 'Listing', key: 'listing', children: showingTable },
  ];

  return (
    <Section data-cy={'showing-section'}>
      <StyledCard
        title="Showings"
        $hideBorderBottom
        extra={
          <StyledContainer>
            {!!totalShowingCount && (
              <Text type="secondary">
                {showings.length} of {totalShowingCount}
              </Text>
            )}
            <Input
              prefix={<Icon type="search" color="gray9" offset={[0, -1]} />}
              onChange={e => setSearchText(e.target.value)}
              value={searchText}
              type="text"
              placeholder="Search showings"
            />
            {featureEnabled('supraAutoCoopFeature') &&
              user?.coopType === SupraCoopType.PRIMARY && (
                <Link
                  onClick={answeringConfirm({ type: 'agent' }, () => {
                    CHAT_SESSION_STORAGE_KEYS.forEach(key =>
                      sessionStorage.removeItem(key),
                    );
                    window.location.href = `/answering/assume_user/${userId}?${paramaterize(
                      {
                        redirectUrl: encodeURIComponent(
                          `/my_business/showings/${tabKey}`,
                        ),
                      },
                    )}`;
                  })}
                >
                  View all
                </Link>
              )}
          </StyledContainer>
        }
      >
        <Tabs
          defaultActiveKey={tabKey}
          onChange={activeKey => {
            setTabKey(activeKey as TabKeyType);
            setTablePage(1);
          }}
          items={tabItems}
          tabBarStyle={{ margin: 0, padding: '0 25px' }}
        />
      </StyledCard>
    </Section>
  );
};

export default ShowingSection;

const StyledPagination = styled(Pagination)`
  margin-top: 5px;
  margin-bottom: 5px;
  text-align: center;
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
